import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  useMutation,
  MutationKey,
  MutateOptions,
} from "react-query";

import {
  applicationAppsService,
  applicationCreateService,
  applicationDetailService,
  applicationListShortService,
  applicationUpdateService,
  applicationUpdateStatusService,
} from "./services";

type ApplicationAppsResponse = Awaited<
  ReturnType<typeof applicationAppsService>
>;
type ApplicationShortListResponse = Awaited<
  ReturnType<typeof applicationListShortService>
>;
type ApplicationDetailResponse = Awaited<
  ReturnType<typeof applicationDetailService>
>;

export const useApplications = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof applicationAppsService>[0];
  options?: QueryObserverOptions<unknown, unknown, ApplicationAppsResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "applicationsHook",
    queryFn: () => applicationAppsService(params),
    ...options,
  });

  return response as UseQueryResult<ApplicationAppsResponse>;
};

export const useShortApplication = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof applicationListShortService>[0];
  options?: QueryObserverOptions<
    unknown,
    unknown,
    ApplicationShortListResponse
  >;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "applicationShortListHooks",
    queryFn: () => applicationListShortService(params),
    ...options,
  });

  return response as UseQueryResult<ApplicationShortListResponse>;
};

export const useApplicationDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof applicationDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, ApplicationDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "applicationDetailHooks",
    queryFn: () => applicationDetailService(params),
    ...options,
  });

  return response as UseQueryResult<ApplicationDetailResponse>;
};

export const useApplicationUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    ApplicationDetailResponse,
    unknown,
    Parameters<typeof applicationUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "applicationUpdateHooks",
    mutationFn: applicationUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useApplicationCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    ApplicationDetailResponse,
    unknown,
    Parameters<typeof applicationCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "applicationCreateHooks",
    mutationFn: applicationCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useApplicationUpdateStatus = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    ApplicationDetailResponse,
    unknown,
    Parameters<typeof applicationUpdateStatusService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "applicationUpdateStatusService",
    mutationFn: applicationUpdateStatusService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
